
import Vue from 'vue';
import Component from 'vue-class-component';
import IconChip from '@/core/components/IconChip.vue';
import PaymentChip from '@/core/components/PaymentChip.vue';
import InputSlider from '@/core/components/InputSlider.vue';
import DollarInput from '@/core/components/DollarInput.vue';
import { namespace } from 'vuex-class';
import { LoanDetails } from '@/loan/types/loan-details';
import onetimeIconSVG from '@/assets/images/onetimeIcon.svg';
import LoanProjectionChart from '@/loan/components/LoanProjectionChart.vue';
import { LoanProjection } from '@/loan/types/loan-projection';
import { LoanProjectionRequestQuery } from '@/loan/types/loan-projection-request-query';
import { Debouncer } from '@/core/types/debouncer';
import { LoanProjectionType } from '@/loan/enums/loan-projection-type';
import { LoanProjectionChartContext } from '@/loan/types/loan-projection-chart-context';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');

const MINIMUM_NEXT_PAYMENT_AMOUNT = 0.01;
const LESS_THAN_MINIMUM_PAYMENT_AMOUNT_TEXT = `Your One-Time payment must be at least $${MINIMUM_NEXT_PAYMENT_AMOUNT}.`;
const GREATER_THAN_PAYOFF_AMOUNT_TEXT = 'Your One-Time payment must be at or below your payoff amount and $55,000.00.';
const ICON_CHIP_TITLE = 'Got Extra Money?';
const ICON_CHIP_TEXT = 'Every dollar paid above what you owe helps build your Take-Back® balance.';
const PAST_DUE_ICON_CHIP_TITLE = 'Your loan is past due.';
const PAST_DUE_ICON_CHIP_TEXT = 'Make a payment to get back on track.';
const PAYMENT_CHIP_TEXT = (dateForDisplayString: string) => `Due on ${dateForDisplayString}`;
const PAST_DUE_PAYMENT_CHIP_TEXT = (daysDelinquent: number) => `${daysDelinquent} Days Past Due`;
const PAYMENT_CHIP_FEES_TOOLTIP_TEXT = 'Want to pay this fee? When you make a payment, any outstanding principal and interest gets paid first. Your payment amount should include your amount due for this period (shown here) and the fee amount in order to pay the fee.';
const TEST_ID = 'Kloans-Loan-LoanOneTime';

@Component({
	components: {
		IconChip,
		PaymentChip,
		InputSlider,
		DollarInput,
		LoanProjectionChart,
	},
})
export default class LoanOneTime extends Vue {
	public TEST_ID = TEST_ID;

	@loanNamespace.Getter('calculatedAmountPastDue')
	public calculatedAmountPastDue!: number;

	@loanNamespace.Getter('daysDelinquent')
	public daysDelinquent!: number;

	public defaultNextPaymentInputValue = 0;

	public formatDollar = UtilHelper.formatDollar;

	@loanNamespace.Action('getAndSetProjection')
	public getAndSetProjection!: (
		{ loanId, query }:
		{ loanId: string; query: LoanProjectionRequestQuery }
	) => Promise<void>;

	@loanNamespace.Getter('hasOutstandingFees')
	public hasOutstandingFees!: boolean;

	public iconChipIcon = onetimeIconSVG;

	public iconChipText = ICON_CHIP_TEXT;

	public iconChipTitle = ICON_CHIP_TITLE;

	@loanNamespace.Getter('isAutopayOn')
	public isAutopayOn!: boolean;

	@loanNamespace.Getter('isPastDue')
	public isPastDue!: boolean;

	@loanNamespace.Getter('isPastDueWithPendingTransactions')
	public isPastDueWithPendingTransactions!: boolean;

	public isProjectionLoading = false;

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	public nextPaymentInputDebouncer!: Debouncer<number>;

	public nextPaymentInputErrorMessage: string | null = null;

	public nextPaymentInputMin = 0.01;

	public nextPaymentInputValue = 0;

	public maxDwollaPaymentAmount = 55000;

	@loanNamespace.Getter('outstandingFees')
	public outstandingFees!: number;

	@loanNamespace.Getter('payoffAmount')
	public payoffAmount!: number;

	public pastDueIconChipText = PAST_DUE_ICON_CHIP_TEXT;

	public pastDueIconChipTitle = PAST_DUE_ICON_CHIP_TITLE;

	public pastDuePaymentChipDueText: string = '';

	public paymentChipDueText: string = '';

	public paymentChipFeesTooltipText = PAYMENT_CHIP_FEES_TOOLTIP_TEXT;

	@loanNamespace.State('projection')
	public projection!: LoanProjection;

	public projectionChartContext: LoanProjectionChartContext | null = null;

	@loanNamespace.Getter('startBalance')
	public startBalance!: number;

	public wasProjectionUpdated = false;

	get isNextPaymentButtonDisabled(): boolean {
		return (this.nextPaymentInputValue < this.nextPaymentInputMin)
			|| (this.nextPaymentInputValue > this.payoffAmount)
			|| (this.nextPaymentInputValue > this.maxDwollaPaymentAmount);
	}

	get nextPaymentInputMax(): string {
		if (Number(this.loanDetails.general.payoffAmount) <= this.maxDwollaPaymentAmount) {
			return this.loanDetails.general.payoffAmount;
		}

		return Math.floor(this.maxDwollaPaymentAmount).toString();
	}

	get paymentChipOutstandingFees(): string | null {
		return (this.hasOutstandingFees && !this.isPastDue)
			? this.formatDollar(this.outstandingFees)
			: null;
	}

	get paymentAmount(): number {
		return this.isPastDueWithPendingTransactions
			? this.calculatedAmountPastDue
			: Number(this.loanDetails.general.remainingInstallmentDue);
	}

	get paymentDueDateISOString(): string {
		return this.loanDetails.general.paymentDueDate;
	}

	mounted(): void {
		this.nextPaymentInputDebouncer = new Debouncer({
			callback: this.setProjectionContextFromInput,
			debounceTime: 300,
			skipIfSame: true,
		});

		this.paymentChipDueText = PAYMENT_CHIP_TEXT(UtilHelper.formatISOStringForDisplay(this.paymentDueDateISOString));
		this.pastDuePaymentChipDueText = PAST_DUE_PAYMENT_CHIP_TEXT(this.daysDelinquent);
		this.defaultNextPaymentInputValue = this.isAutopayOn ? 0 : Number(this.loanDetails.general.installmentPastDue);
		this.nextPaymentInputValue = this.defaultNextPaymentInputValue;

		this.setProjectionContextFromInput(this.nextPaymentInputValue, this.isAutopayOn);
	}

	public async setProjectionContextFromInput(value: number, isOriginal = false): Promise<void> {
		this.isProjectionLoading = true;

		const query: LoanProjectionRequestQuery = {
			property: LoanProjectionType.ONE_TIME,
			value: String(value),
		};

		await this.getAndSetProjection({
			loanId: this.loanId,
			query,
		});

		this.projectionChartContext = {
			isOriginal,
			projection: this.projection,
		};

		this.isProjectionLoading = false;
	}

	public async onNextPaymentInputChange(value: number): Promise<void> {
		if (this.nextPaymentInputValue === value) {
			return;
		}

		this.nextPaymentInputValue = value;
		this.nextPaymentInputErrorMessage = null;

		if (
			!this.wasProjectionUpdated
			&& !this.isAutopayOn
			&& this.nextPaymentInputValue < Number(this.calculatedAmountPastDue)
		) {
			return;
		}

		if (this.nextPaymentInputValue < MINIMUM_NEXT_PAYMENT_AMOUNT) {
			this.nextPaymentInputErrorMessage = LESS_THAN_MINIMUM_PAYMENT_AMOUNT_TEXT;
			return;
		}

		if (this.nextPaymentInputValue > this.payoffAmount) {
			this.nextPaymentInputErrorMessage = GREATER_THAN_PAYOFF_AMOUNT_TEXT;
			return;
		}

		if (this.nextPaymentInputValue > this.maxDwollaPaymentAmount) {
			this.nextPaymentInputErrorMessage = GREATER_THAN_PAYOFF_AMOUNT_TEXT;
			return;
		}

		if (this.isAutopayOn) {
			this.nextPaymentInputDebouncer.debounce(value);
			this.wasProjectionUpdated = true;
			return;
		}

		if (this.wasProjectionUpdated) {
			this.nextPaymentInputDebouncer.debounce(value);
			return;
		}

		if (value > this.calculatedAmountPastDue) {
			this.nextPaymentInputDebouncer.debounce(value);
			this.wasProjectionUpdated = true;
		}
	}
}

